import React from 'react'
import dynamic from 'next/dynamic'
import cx from 'classnames'
import styles from './Form.module.scss'
import { FormProps, ModalViewFormSuccessProps } from '~types'
import { FORM_ERROR_VIEW } from 'constants/modal-form-error'
import { isValidEmail } from 'utils'

// @ts-ignore
const ReactFormstack = dynamic(() => import('@msgtechnology/react-formstack').then(module => module.default), {
  ssr: false
}) as unknown as React.ComponentType<Record<string, unknown>>

interface FormstackData {
  id: string
}

const Form = ({
  formId,
  isChildOfModal = false,
  submitButtonBgColor = 'white',
  submitButtonText = 'Sign Up Now',
  successModalView,
  fieldEnhancements = {},
  floatingLabel = false,
  narrowForm = false,
  onSuccess,
  onError
}: FormProps) => {
  const formStackApiUrl = process.env.NEXT_PUBLIC_FORMSTACK_API_URL
  if (!formStackApiUrl || !formId) return null

  const extractEmailValue = (data: { data?: { value: string }[] }): string => {
    return data?.data?.find(({ value }) => typeof value === 'string' && isValidEmail(value))?.value ?? ''
  }

  const buildSuccessView = (view: ModalViewFormSuccessProps | undefined, email: string): ModalViewFormSuccessProps => {
    if (!view) return FORM_ERROR_VIEW // fallback
    const replacedHtml = view.description?.html?.replace(/{emailAddress}/g, email) ?? ''
    return {
      ...view,
      description: { ...view.description, html: replacedHtml }
    }
  }

  const buildErrorView = (view: ModalViewFormSuccessProps | undefined): ModalViewFormSuccessProps => ({
    ...FORM_ERROR_VIEW,
    ...(view?.componentName && { componentName: view?.componentName }),
    ...(view?.id ? { id: view.id } : {})
  })

  const afterSubmit = (data: FormstackData & { data?: { field: string; value: string }[] }) => {
    const isSuccessful = Boolean(data?.id)
    if (isSuccessful) {
      onSuccess?.(buildSuccessView(successModalView, extractEmailValue(data)))
    } else {
      onError?.(buildErrorView(successModalView))
    }
  }

  const formClassNames = cx([
    'formstack',
    'formstack--input-bg-color-transparent',
    `formstack--submit-btn-bg-${submitButtonBgColor}`,
    isChildOfModal && 'formstack--is-child-of-modal',
    styles['fs-field-wrapper'],
    styles['fs-label'],
    floatingLabel && 'floating-label',
    narrowForm && 'narrow-form'
  ])

  const FEisEmpty =
    !fieldEnhancements ||
    (Object.getPrototypeOf(fieldEnhancements) === Object.prototype && Object.keys(fieldEnhancements).length === 0)

  const defaults = {
    email: {},
    phone: {},
    text: {},
    textarea: {},
    name: {},
    select: {
      useSelect2Ui: false
    },
    radio: {},
    checkbox: {},
    datetime: {
      validationMessage: 'Please enter a valid date.'
    }
  }

  const formFieldEnhancements = FEisEmpty ? defaults : fieldEnhancements ?? defaults

  return (
    <ReactFormstack
      className={formClassNames}
      id={formId.toString()}
      onAfterSubmit={afterSubmit}
      submitButtonText={submitButtonText}
      endpoint={formStackApiUrl}
      fieldEnhancements={formFieldEnhancements}
      disableBrowserValidation={true}
      shouldResetOnSubmit={true}
    />
  )
}

export default Form
